import React, { useState, useEffect } from 'react';
import ReactImageUploading from 'react-images-uploading';
import { Box, Typography, Button, Stack } from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import api, { FILES_URL } from 'src/Api';
import Image from 'src/components/Image';
import { useTheme } from '@mui/material/styles';

export default function Step1({ user, setUser, setUnVerifiedSteps, setActiveStep }) {
  const [picture, setPicture] = useState([]);
  const theme = useTheme();
  const onPictureChange = (imageList) => {
    setPicture(imageList);
  };

  useEffect(() => {
    if (picture.length > 0) {
      submit();
    }
  }, [picture]);

  const showError = msg => {
    toast.error(msg, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const showSuccess = msg => {
    toast.success(msg, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const submit = () => {
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    };
    let body = new FormData();

    body.append('picture', picture[0]?.file);
    body.append('step', 1);

    api.post('/users/submit_step', body, config)
      .then(res => {
        setUser(res.data);
        setUnVerifiedSteps(res.data.unverified_steps);
        showSuccess('Opération réussie');
      })
      .catch(err => {
        showError('Une erreur s\'est produite.');
      });
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      width: '100%',
      maxWidth: '800px',
      margin: '0 auto',
      py: 4
    }}>
      <Typography variant="h4" component="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
        Photo de profil
      </Typography>
      
      <Typography variant="p" component="p" sx={{ color: 'text.secondary', mb: 6 }}>
        Ajouter une photo de profil
      </Typography>

      <ReactImageUploading
        value={picture}
        onChange={onPictureChange}
        maxNumber={1}
        dataURLKey="data_url"
        acceptType={['jpg', 'png', 'jpeg']}
      >
        {({
          imageList,
          onImageUpload,
          onImageUpdate,
          isDragging,
          dragProps,
        }) => (
          <Box
            sx={{
              width: '100%',

              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '300px',
                border: '2px dashed #ccc',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
                p: 3,
                backgroundColor: 'transparent'
              }}
              {...dragProps}
            >
              {imageList.length === 0 && !user?.avatar?.length && (
                <>
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '60px',
                      height: '60px',
                      mb: 2
                    }}
                  >
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M30 12.5V35M30 12.5L22.5 20M30 12.5L37.5 20" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M45 25C50 28.5 52.5 35 47.5 41.25C42.5 47.5 35 47.5 30 47.5C25 47.5 17.5 47.5 12.5 41.25C7.5 35 10 28.5 15 25" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </Box>
                  <Typography variant="h6" component="p" sx={{ fontWeight: 'medium', mb: 1 }}>
                    Selectionne une photo
                  </Typography>
                  <Typography variant="body1" component="p" sx={{ color: 'text.secondary', mb: 4 }}>
                    Seulement les fichiers JPG ou PNG sont autorisés
                  </Typography>
                </>
              )}
              
              {imageList.length === 0 && user?.avatar?.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Image 
                    src={FILES_URL + user.avatar}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%', 
                      objectFit: 'contain' 
                    }} 
                  />
                </Box>
              )}
              {imageList.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Image 
                    src={image.data_url} 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%', 
                      objectFit: 'contain' 
                    }} 
                  />
                </Box>
              ))}
            </Box>

            <Button
              sx={{
                textTransform: 'uppercase',
                backgroundColor: '#FFECC5',
                color: '#E4A951',
                borderRadius: '4px',
                py: 1.5,
                px: 4,
                border: '1px solid #E4A951',
                '&:hover': {
                  backgroundColor: '#FFE4B0',
                }
              }}
              onClick={() => {
                if (imageList.length > 0) {
                  onImageUpdate(0);
                } else {
                  onImageUpload();
                }
              }}
            >
              Choisir une image
            </Button>
          </Box>
        )}
      </ReactImageUploading>

      <Button
        variant="contained"
        sx={{
          textTransform: 'none',
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          fontWeight: 'bold',
          borderRadius: '4px',
          py: 1.8,

          width: '100%',
          fontSize: '1rem',
          mt: 4,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
          '&.Mui-disabled': {
            backgroundColor: '#F0B254',
            opacity: 0.7,
            color: 'white',
          }
        }}
        disabled={picture.length === 0 && !user?.avatar?.length}
        onClick={() => {
          setActiveStep && setActiveStep(2);
        }}
      >
        Suivant
      </Button>

      <ToastContainer
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Box>
  );
}